import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export const STAFF_THEME_STORAGE_KEY = 'domcrm-staff-theme';

export type StaffResolvedTheme = 'light' | 'dark';

type StaffThemeContextValue = {
  theme: StaffResolvedTheme;
  setTheme: (next: StaffResolvedTheme) => void;
  toggleTheme: () => void;
};

const StaffThemeContext = createContext<StaffThemeContextValue | null>(null);

function readThemeFromDocument(): StaffResolvedTheme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function StaffThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<StaffResolvedTheme>(readThemeFromDocument);

  const applyDom = useCallback((next: StaffResolvedTheme) => {
    document.documentElement.setAttribute('data-theme', next);
    document.documentElement.style.colorScheme = next;
  }, []);

  const setTheme = useCallback(
    (next: StaffResolvedTheme) => {
      setThemeState(next);
      try {
        localStorage.setItem(STAFF_THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      applyDom(next);
    },
    [applyDom],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [setTheme, theme]);

  useEffect(() => {
    applyDom(theme);
  }, [applyDom, theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [setTheme, theme, toggleTheme],
  );

  return <StaffThemeContext.Provider value={value}>{children}</StaffThemeContext.Provider>;
}

export function useStaffTheme(): StaffThemeContextValue {
  const ctx = useContext(StaffThemeContext);
  if (!ctx) {
    throw new Error('useStaffTheme: оберните приложение в StaffThemeProvider');
  }
  return ctx;
}
